import { Select } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import {
  ACCOUNTING_STATUSES,
  ACCOUNTING_STATUS_STYLES,
  DEFAULT_ACCOUNTING_STATUS,
} from '../../constants/paymentVoucher';
import './PaymentVoucher.scss';

interface AccountingStatusControlProps {
  value?: string;
  /** Accountant / Super Admin get the dropdown; everyone else a static pill. */
  editable?: boolean;
  saving?: boolean;
  disabled?: boolean;
  /** Fill the width of a table cell instead of hugging the label. */
  block?: boolean;
  onChange?: (next: string) => void;
}

/**
 * Accounting status control. Editable mode matches the CMS Select dropdown UI
 * (white field, clear border, navy focus). Static mode keeps a status pill.
 */
export function AccountingStatusControl({
  value,
  editable = false,
  saving = false,
  disabled = false,
  block = false,
  onChange,
}: AccountingStatusControlProps) {
  const status = value || DEFAULT_ACCOUNTING_STATUS;
  const palette =
    ACCOUNTING_STATUS_STYLES[status] || ACCOUNTING_STATUS_STYLES[DEFAULT_ACCOUNTING_STATUS];

  const className = [
    'pv-accounting-status',
    editable ? 'pv-accounting-status--editable' : 'pv-accounting-status--static',
    saving ? 'pv-accounting-status--saving' : '',
    block ? 'pv-accounting-status--block' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!editable) {
    return (
      <span
        className={className}
        style={{
          background: palette.background,
          borderColor: palette.border,
          color: palette.text,
        }}
        title={`Accounting status: ${status}`}
      >
        {saving ? (
          <LoadingOutlined style={{ fontSize: 13, color: palette.text }} spin />
        ) : (
          <span className="pv-accounting-status__dot" style={{ background: palette.dot }} />
        )}
        <span className="pv-accounting-status__label">{status}</span>
      </span>
    );
  }

  return (
    <span className={className}>
      <Select
        value={status}
        size="large"
        disabled={disabled || saving}
        onChange={onChange}
        dropdownMatchSelectWidth={false}
        dropdownClassName="select-checkbox-dropdown pv-accounting-status-dropdown"
        suffixIcon={
          saving ? <LoadingOutlined style={{ fontSize: 12, color: '#004195' }} spin /> : undefined
        }
        options={ACCOUNTING_STATUSES.map(option => {
          const optionPalette =
            ACCOUNTING_STATUS_STYLES[option] || ACCOUNTING_STATUS_STYLES[DEFAULT_ACCOUNTING_STATUS];
          return {
            value: option,
            label: (
              <span className="pv-accounting-status__option">
                <span
                  className="pv-accounting-status__dot"
                  style={{ background: optionPalette.dot }}
                />
                {option}
              </span>
            ),
          };
        })}
      />
    </span>
  );
}

export default AccountingStatusControl;
